import React, { useCallback, useContext, useMemo } from 'react';
import { EDAutoCompleteEdit } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import { OnChange } from '../../../../../../../Components/Attributes/EditableAttribute/EditComponentTypes';
import { TEDEKBokforderTYPEnum } from '../../../../../../../globalQueryTypes';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { NEWLY_ADDED_ID_PREFIX_FOR_UPDATE } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Const/BusinessDataFormConst';
import { FormUpdateAssociatonType } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { IKodstrangDel } from '../../../../../_Common/Components/KonteringManager/KonteringManager';
import { IKontodelDef } from '../../../../../_Common/Components/KonteringManager/Query/KonteringManagerQuery';
import { BokforderType__Associations_Rader } from '../../Queries/_QueryTypes/BokforderType';
import {
  createKodstrangDelarFromRow,
  filterUsersOnKodstrang,
  isRowEmpty,
} from '../../Utils/BokforderUtils';
import BokforderContext from '../BokforderContext/BokforderContext';
import { IUserBokforderKodbehorigheter } from '../BokforderContext/BokforderContextManager';

interface IProps {
  isDisabled?: boolean;
  kontodelDefFromKodplan: IKontodelDef[];
  setValdAttestant: React.Dispatch<
    React.SetStateAction<IUserBokforderKodbehorigheter | null>
  >;
  type: TEDEKBokforderTYPEnum;
  valdAttestant: IUserBokforderKodbehorigheter | null;
  addAttestantOnEmptyRow?: boolean;
}

export const AttestantPicker: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { setValdAttestant } = props;
  const bokforderContext = useContext(BokforderContext);
  const formContext = useContext(BusinessDataFormContext);
  const formContextUpdate = formContext?.update;

  const attestantOptions: IUserBokforderKodbehorigheter[] = useMemo(() => {
    let availableAttestanter =
      props.type === TEDEKBokforderTYPEnum.BOKFORINGSORDER
        ? bokforderContext.allBokforderUsersWithKodbeh
        : bokforderContext.allUtanordningUsersWithKodbeh;

    const rowsInGrid: BokforderType__Associations_Rader[] | null | undefined =
      formContext?.formState.currentValues?._Associations?.Rader;

    if (rowsInGrid) {
      for (let index = 0; index < rowsInGrid.length; index++) {
        const row = rowsInGrid[index];

        let kontodelarInKodstrang: IKodstrangDel[] =
          createKodstrangDelarFromRow(
            row._Associations,
            props.kontodelDefFromKodplan
          );

        if (kontodelarInKodstrang.length > 0) {
          availableAttestanter = availableAttestanter.filter(
            filterUsersOnKodstrang(kontodelarInKodstrang)
          );
        }
      }
    }

    return availableAttestanter;
  }, [
    bokforderContext.allBokforderUsersWithKodbeh,
    bokforderContext.allUtanordningUsersWithKodbeh,
    formContext?.formState.currentValues?._Associations?.Rader,
    props.kontodelDefFromKodplan,
    props.type,
  ]);

  const onChangeAttestant: OnChange<IUserBokforderKodbehorigheter> =
    useCallback(
      option => {
        if (option != null) {
          const rowsInGrid: BokforderType__Associations_Rader[] =
            formContext?.formState.currentValues?._Associations?.Rader;

          const rowFormUpdates: FormUpdateAssociatonType[][] =
            rowsInGrid.reduce<FormUpdateAssociatonType[][]>((prev, current) => {
              if (!isRowEmpty(current, false) || props.addAttestantOnEmptyRow) {
                const newValue: FormUpdateAssociatonType[] = [
                  {
                    associationName: 'Rader',
                    operation: current.id.includes(
                      NEWLY_ADDED_ID_PREFIX_FOR_UPDATE
                    )
                      ? 'Add_Values'
                      : 'Update_Values',
                    id: current.id,
                  },
                  {
                    associationName: 'Attestant',
                    operation: 'Ref',
                    values: { id: option?.userID },
                  },
                ];
                return [...prev, newValue];
              } else {
                return prev;
              }
            }, []);
          formContextUpdate?.(rowFormUpdates);
          setValdAttestant(option);
        } else {
          const rowsInGrid: BokforderType__Associations_Rader[] =
            formContext?.formState.currentValues?._Associations?.Rader;

          if (rowsInGrid) {
            const rowFormUpdates: FormUpdateAssociatonType[][] =
              rowsInGrid.reduce<FormUpdateAssociatonType[][]>(
                (prev, current) => {
                  if (
                    (!isRowEmpty(current, false) ||
                      props.addAttestantOnEmptyRow) &&
                    current._Associations?.Attestant?.id
                  ) {
                    const newValue: FormUpdateAssociatonType[] = [
                      {
                        associationName: 'Rader',
                        operation: current.id.includes(
                          NEWLY_ADDED_ID_PREFIX_FOR_UPDATE
                        )
                          ? 'Add_Values'
                          : 'Update_Values',
                        id: current.id,
                      },
                      {
                        associationName: 'Attestant',
                        operation: 'Ref',
                        id: null,
                      },
                    ];
                    return [...prev, newValue];
                  } else {
                    return prev;
                  }
                },
                []
              );
            formContextUpdate?.(rowFormUpdates);

            setValdAttestant(null);
          }
        }
      },
      [
        formContextUpdate,
        formContext?.formState.currentValues?._Associations?.Rader,
        props.addAttestantOnEmptyRow,
        setValdAttestant,
      ]
    );
  const hasOnlyEmptyRows = useMemo(() => {
    const rowsInGrid: BokforderType__Associations_Rader[] =
      formContext?.formState.currentValues?._Associations?.Rader;

    const onlyEmptyRows = rowsInGrid?.every(row => {
      return isRowEmpty(row, false);
    });

    return onlyEmptyRows;
  }, [formContext?.formState.currentValues?._Associations?.Rader]);

  return (
    <EDTooltip
      title={
        hasOnlyEmptyRows
          ? 'Det finns inga rader där attestant kan fyllas på'
          : undefined
      }
      enableClickOnTouch={props.isDisabled || hasOnlyEmptyRows}
    >
      <EDBox component={'span'} sx={{ display: 'block' }}>
        <EDAutoCompleteEdit<IUserBokforderKodbehorigheter>
          disabled={props.isDisabled || hasOnlyEmptyRows}
          fullWidth
          getOptionLabel={option => {
            return option.namn;
          }}
          label="Attestant"
          options={attestantOptions}
          onChange={onChangeAttestant}
          value={props.valdAttestant}
          testId="attestant-picker"
        />
      </EDBox>
    </EDTooltip>
  );
};

export default AttestantPicker;
